// src/components/banner/BannerSlider.js
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import {  doc as firestoreDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';

export default function BannerSlider() {
  const swiperRef = useRef();
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const perGroup = 3;

  useEffect(() => {
    const fetchBanners = async () => {
      const bannerListRef = firestoreDoc(db, 'data', 'bannerList');
      const bannerListSnap = await getDoc(bannerListRef);
      const bannerIds = bannerListSnap.exists() ? bannerListSnap.data().banner || [] : [];

      const storage = getStorage();
      const docs = await Promise.all(
        bannerIds.map(async (id) => {
          const cacheKey = `bannerImage_${id}`;
          // localStorage에서 캐시된 URL 확인
          let imageUrl = localStorage.getItem(cacheKey) || '';

          const bannerDocRef = firestoreDoc(db, 'banners', id);
          const bannerSnap = await getDoc(bannerDocRef);
          if (!bannerSnap.exists()) return null;
          const data = bannerSnap.data();

          if (!imageUrl && data.bannerPath) {
            if (data.bannerPath.startsWith('http://') || data.bannerPath.startsWith('https://')) {
              imageUrl = data.bannerPath;
            } else {
              try {
                imageUrl = await getDownloadURL(storageRef(storage, data.bannerPath));
              } catch {
                imageUrl = '';
              }
            }
            if (imageUrl) {
              try {
                localStorage.setItem(cacheKey, imageUrl);
              } catch {}
            }
          }

          return {
            id: bannerSnap.id,
            linkId: data.linkId,
            image: imageUrl,
            label: data.tag,
            title: data.title,
            subtitle: data.subtitle,
          };
        })
      );

      setBanners(docs.filter((b) => b !== null));
    };

    fetchBanners();
  }, []);

  const displayBanners = useMemo(() => {
    if (!banners.length) return [];
    if (banners.length <= perGroup) return banners;
    const rem = banners.length % perGroup;
    if (rem === 0) return banners;
    return [...banners, ...banners.slice(0, perGroup - rem)];
  }, [banners, perGroup]);

  const enableLoop = banners.length > perGroup;
  const groupCount = Math.ceil(displayBanners.length / perGroup);

  const handlePrev = () => {
    const swiper = swiperRef.current;
    if (!swiper) return;
    const real = swiper.realIndex;
    const grp = Math.floor(real / perGroup);
    if (grp === 0) {
      swiper.slideToLoop((groupCount - 1) * perGroup);
    } else {
      swiper.slidePrev();
    }
  };

  const handleNext = () => {
    const swiper = swiperRef.current;
    if (!swiper) return;
    const real = swiper.realIndex;
    const grp = Math.floor(real / perGroup);
    if (grp === groupCount - 1) {
      swiper.slideToLoop(0);
    } else {
      swiper.slideNext();
    }
  };

  return (
    <div className="w-full flex justify-center">
      <div className="w-full">
        <Swiper
          modules={[Pagination]}
          loop={enableLoop}
          slidesPerView={perGroup}
          slidesPerGroup={perGroup}
          spaceBetween={24}
          onSwiper={(s) => {
            swiperRef.current = s;
          }}
          pagination={{
            el: '.banner-pagination',
            clickable: true,
            renderBullet: (i, className) =>
              `<span class="${className} w-2 h-2 inline-block mx-1 bg-gray-200 rounded-full transition-all duration-300"></span>`,
          }}
          breakpoints={{
            0: { slidesPerView: 1, slidesPerGroup: 1 },
            940: { slidesPerView: 2, slidesPerGroup: 2 },
            1535: { slidesPerView: perGroup, slidesPerGroup: perGroup },
          }}
          className="w-full"
        >
          {displayBanners.map((banner, i) => (
            <SwiperSlide key={`${banner.id}-${i}`}>
              <div className="flex justify-center">
                <div
                  className="relative rounded-2xl shadow-lg overflow-hidden bg-black
                             flex items-end aspect-banner w-full max-w-[calc(100%)] cursor-pointer 
                             transition-all duration-200"
                  onClick={() => navigate(`/${banner.linkId}`)}
                >
                  {/* 이미지 레이어 */}
                  <div className="absolute inset-0">
                    {banner.image ? (
                      <img
                        src={banner.image}
                        alt={banner.title}
                        className="w-full h-full object-cover object-center"
                        draggable={false}
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="flex justify-center items-center w-full h-full bg-gray-700">
                        <span className="text-white text-sm">이미지 없음</span>
                      </div>
                    )}

                    {/* 반투명 오버레이 */}
                    <div className="absolute inset-0 bg-black bg-opacity-50"></div>
                  </div>

                  {/* 텍스트 레이어 */}
                  <div className="relative z-10 p-6 text-white w-full">
                    <div className="mb-2 text-xs bg-black/70 border border-neutral-400 rounded-full px-3 py-1 inline-block font-semibold">
                      {banner.label}
                    </div>
                    <div className="font-bold text-2xl md:text-3xl drop-shadow-lg mb-2 whitespace-pre-wrap">
                      {banner.title}
                    </div>
                    <div className="text-sm drop-shadow-md opacity-90 whitespace-pre-wrap">
                      {banner.subtitle}
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="flex items-center gap-4 mt-3 mb-4 w-fit mx-auto">
          <button
            className="bg-neutral-700/70 text-white rounded-full p-2 hover:bg-neutral-500 transition"
            onClick={handlePrev}
          >
            <FaChevronLeft size={18} />
          </button>
          <div className="banner-pagination flex items-center gap-2" />
          <button
            className="bg-neutral-700/70 text-white rounded-full p-2 hover:bg-neutral-500 transition"
            onClick={handleNext}
          >
            <FaChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
